import {
  Banknote,
  Building2,
  Calculator,
  Droplets,
  Factory,
  type LucideIcon,
  PieChart,
  Plane,
  ShoppingCart,
  Store,
  Upload,
  Users,
  Warehouse,
  Zap,
} from 'lucide-react';
import xeroLogo from '../../../../images/vendor-logos/xero.svg';
import quickbooksLogo from '../../../../images/vendor-logos/quickbooks.svg';
import sageLogo from '../../../../images/vendor-logos/sage.svg';
import oracleLogo from '../../../../images/vendor-logos/oracle.svg';
import octopusLogo from '../../../../images/vendor-logos/octopus.svg';
import britishGasLogo from '../../../../images/vendor-logos/british-gas.svg';
import eonLogo from '../../../../images/vendor-logos/eon.svg';
import edfLogo from '../../../../images/vendor-logos/edf.svg';
import concurLogo from '../../../../images/vendor-logos/concur.svg';
import navanLogo from '../../../../images/vendor-logos/navan.svg';
import spGroupLogo from '../../../../images/vendor-logos/sp-group.webp';
import genecoLogo from '../../../../images/vendor-logos/geneco.svg';
import tuasPowerLogo from '../../../../images/vendor-logos/tuas-power.png';
import keppelElectricLogo from '../../../../images/vendor-logos/keppel-electric.svg';
import pubLogo from '../../../../images/vendor-logos/pub.png';
import ovoLogo from '../../../../images/vendor-logos/ovo.png';
import scottishPowerLogo from '../../../../images/vendor-logos/scottish-power.png';
import dewaLogo from '../../../../images/vendor-logos/dewa.png';
import secLogo from '../../../../images/vendor-logos/sec.png';
import taqaDistributionLogo from '../../../../images/vendor-logos/taqa-distribution.svg';
import sewgaLogo from '../../../../images/vendor-logos/sewga.png';
import egenciaLogo from '../../../../images/vendor-logos/egencia.png';
import nwcLogo from '../../../../images/vendor-logos/nwc.png';
import type { DataIntent, RequestMethod, SurveyTile } from './types';

export type ProviderRegion = 'SG' | 'UK' | 'UAE' | 'KSA' | 'GLOBAL';

export interface ProviderEntry {
  id: string;
  name: string;
  region: ProviderRegion;
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  note?: string;
  /** When set, the provider card renders this brand mark instead of the lucide Icon. */
  logoUrl?: string;
}

export interface RequestAccessFlowConfig {
  title: string;
  description: string;
  providers: ProviderEntry[];
  groupByRegion: boolean;
  showScanInvoice: boolean;
}

const connectUtilityProviders: ProviderEntry[] = [
  // SG
  { id: 'sp-group', name: 'SP Group', region: 'SG', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', note: 'National grid', logoUrl: spGroupLogo },
  { id: 'geneco', name: 'Geneco', region: 'SG', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: genecoLogo },
  { id: 'tuas-power', name: 'Tuas Power', region: 'SG', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: tuasPowerLogo },
  { id: 'keppel-electric', name: 'Keppel Electric', region: 'SG', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: keppelElectricLogo },
  { id: 'pub-sg', name: 'PUB', region: 'SG', Icon: Droplets, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-600', note: 'Water & waste', logoUrl: pubLogo },

  // UK
  { id: 'octopus', name: 'Octopus Energy', region: 'UK', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: octopusLogo },
  { id: 'british-gas', name: 'British Gas', region: 'UK', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: britishGasLogo },
  { id: 'eon-next', name: 'E.ON Next', region: 'UK', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: eonLogo },
  { id: 'edf', name: 'EDF Energy', region: 'UK', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: edfLogo },
  { id: 'ovo', name: 'OVO Energy', region: 'UK', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: ovoLogo },
  { id: 'scottish-power', name: 'ScottishPower', region: 'UK', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: scottishPowerLogo },

  // UAE
  { id: 'dewa', name: 'DEWA', region: 'UAE', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', note: 'Dubai', logoUrl: dewaLogo },
  { id: 'taqa-distribution', name: 'TAQA Distribution', region: 'UAE', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', note: 'Abu Dhabi (replaces ADDC & AADC)', logoUrl: taqaDistributionLogo },
  { id: 'sewga', name: 'SEWGA', region: 'UAE', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', note: 'Sharjah', logoUrl: sewgaLogo },

  // KSA
  { id: 'sec', name: 'Saudi Electricity Company', region: 'KSA', Icon: Zap, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600', logoUrl: secLogo },
  { id: 'nwc', name: 'National Water Company', region: 'KSA', Icon: Droplets, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-600', note: 'Water', logoUrl: nwcLogo },
];

const connectFinancialProviders: ProviderEntry[] = [
  { id: 'xero', name: 'Xero', region: 'GLOBAL', Icon: Banknote, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-600', logoUrl: xeroLogo },
  { id: 'quickbooks', name: 'QuickBooks', region: 'GLOBAL', Icon: Banknote, iconBg: 'tw:bg-emerald-100', iconColor: 'tw:text-emerald-600', logoUrl: quickbooksLogo },
  { id: 'netsuite', name: 'Oracle NetSuite', region: 'GLOBAL', Icon: Calculator, iconBg: 'tw:bg-red-100', iconColor: 'tw:text-red-600', logoUrl: oracleLogo },
  { id: 'sage', name: 'Sage', region: 'GLOBAL', Icon: Calculator, iconBg: 'tw:bg-lime-100', iconColor: 'tw:text-lime-600', logoUrl: sageLogo },
];

const connectTravelProviders: ProviderEntry[] = [
  { id: 'navan', name: 'Navan', region: 'GLOBAL', Icon: Plane, iconBg: 'tw:bg-violet-100', iconColor: 'tw:text-violet-600', note: 'formerly TripActions', logoUrl: navanLogo },
  { id: 'concur', name: 'SAP Concur', region: 'GLOBAL', Icon: Plane, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-600', logoUrl: concurLogo },
  { id: 'egencia', name: 'Egencia', region: 'GLOBAL', Icon: Plane, iconBg: 'tw:bg-cyan-100', iconColor: 'tw:text-cyan-600', logoUrl: egenciaLogo },
  { id: 'cwt', name: 'CWT', region: 'GLOBAL', Icon: Plane, iconBg: 'tw:bg-indigo-100', iconColor: 'tw:text-indigo-600' },
];

const bySpendMock: ProviderEntry[] = [
  { id: 'procurement-csv', name: 'Procurement CSV', region: 'GLOBAL', Icon: ShoppingCart, iconBg: 'tw:bg-rose-100', iconColor: 'tw:text-rose-600', note: 'Line-item spend → EXIOBASE' },
  { id: 'expense-report', name: 'Expense Report', region: 'GLOBAL', Icon: Upload, iconBg: 'tw:bg-rose-100', iconColor: 'tw:text-rose-600', note: 'Card / expense exports' },
];

const bulkSpendMock: ProviderEntry[] = [
  { id: 'bulk-spend-csv', name: 'Bulk Spend CSV / Excel', region: 'GLOBAL', Icon: Upload, iconBg: 'tw:bg-rose-100', iconColor: 'tw:text-rose-600', note: 'Multi-category template' },
];

const employeeSurveyMock: ProviderEntry[] = [
  { id: 'commuting-survey', name: 'Commuting Survey', region: 'GLOBAL', Icon: Users, iconBg: 'tw:bg-teal-100', iconColor: 'tw:text-teal-600', note: 'Scope 3.7' },
  { id: 'wfh-survey', name: 'Work-from-Home Survey', region: 'GLOBAL', Icon: Building2, iconBg: 'tw:bg-teal-100', iconColor: 'tw:text-teal-600', note: 'Scope 3.7' },
  { id: 'business-travel-survey', name: 'Business Travel Survey', region: 'GLOBAL', Icon: Plane, iconBg: 'tw:bg-teal-100', iconColor: 'tw:text-teal-600', note: 'Scope 3.6' },
];

// Brand-neutral lucide icons are used intentionally while flows are in preview.
// Real brand marks can be swapped in post-GA once licensing is cleared.
export const REQUEST_ACCESS_CONFIG: Record<RequestMethod, RequestAccessFlowConfig> = {
  'connect-utility': {
    title: 'Connect Utility',
    description: 'Auto-sync electricity, gas, and water usage directly from your utility provider. We\'re integrating with major providers across your markets — request access to be notified when your provider is live.',
    providers: connectUtilityProviders,
    groupByRegion: true,
    showScanInvoice: true,
  },
  'connect-financial': {
    title: 'Connect Financial',
    description: 'Auto-sync procurement and expense data from your accounting software for spend-based Scope 3 calculations. Request access to pilot with your finance team.',
    providers: connectFinancialProviders,
    groupByRegion: false,
    showScanInvoice: true,
  },
  'connect-travel': {
    title: 'Connect Travel',
    description: 'Auto-sync business travel and commuting data from your corporate travel platform for Scope 3.6 calculations.',
    providers: connectTravelProviders,
    groupByRegion: false,
    showScanInvoice: false,
  },
  'by-spend': {
    title: 'By Spend',
    description: 'Calculate Scope 3.1 (Purchased Goods & Services) emissions from procurement and expense data using spend-based factors (EXIOBASE via Climatiq).',
    providers: bySpendMock,
    groupByRegion: false,
    showScanInvoice: false,
  },
  'bulk-spend': {
    title: 'Bulk Spend Import',
    description: 'Import spend data for many categories at once from an Excel or CSV template. Auto-classifies line items and maps them to spend-based factors.',
    providers: bulkSpendMock,
    groupByRegion: false,
    showScanInvoice: false,
  },
  'employee-survey': {
    title: 'By Employee Survey',
    description: 'Launch surveys to collect Scope 3.6 business travel and Scope 3.7 commuting / work-from-home data directly from your employees.',
    providers: employeeSurveyMock,
    groupByRegion: false,
    showScanInvoice: false,
  },
};

export const REGION_LABELS: Record<ProviderRegion, string> = {
  SG: 'Singapore',
  UK: 'United Kingdom',
  UAE: 'United Arab Emirates',
  KSA: 'Saudi Arabia',
  GLOBAL: 'Global',
};

export const PROVIDER_REGION_ORDER: ProviderRegion[] = ['SG', 'UK', 'UAE', 'KSA', 'GLOBAL'];

/**
 * Step 1 provider sections, keyed by the intent they live under. Each section
 * sources its providers from the corresponding Step 2 flow config so the
 * shortlist and the catalog stay in sync. The "View all" button on Step 1
 * deep-links to the Step 2 catalog via `catalogMethod` — currently disabled
 * in the UI until we know the full catalog size, but the wiring is ready.
 */
export interface IntentProviderSection {
  title: string;
  hint: string;
  providers: ProviderEntry[];
  catalogMethod: RequestMethod;
  groupByRegion: boolean;
}

export const INTENT_PROVIDER_SECTIONS: Partial<Record<DataIntent, IntentProviderSection>> = {
  activity: {
    title: 'Connect a utility provider',
    hint: 'Electricity, gas, water',
    providers: REQUEST_ACCESS_CONFIG['connect-utility'].providers,
    catalogMethod: 'connect-utility',
    groupByRegion: true,
  },
  spend: {
    title: 'Connect accounting software',
    hint: 'Spend and supplier data',
    providers: REQUEST_ACCESS_CONFIG['connect-financial'].providers,
    catalogMethod: 'connect-financial',
    groupByRegion: false,
  },
  travel: {
    title: 'Connect a travel platform',
    hint: 'Flights, rail, hotels, rideshare',
    providers: REQUEST_ACCESS_CONFIG['connect-travel'].providers,
    catalogMethod: 'connect-travel',
    groupByRegion: false,
  },
};

/**
 * By Survey intent — one flat list of survey templates, no method row.
 * Squashed together (per spec) rather than split into Employee / Supplier
 * subsections until we have enough templates to justify the split.
 */
export const SURVEY_TILES: SurveyTile[] = [
  {
    id: 'suppliers',
    label: 'Suppliers',
    subject: 'Scope 3.1 & 3.2 — Ask suppliers for activity data on goods, services and capital purchases',
    Icon: ShoppingCart,
    iconBg: 'tw:bg-rose-100',
    iconColor: 'tw:text-rose-700',
    titleColor: 'tw:text-rose-900',
  },
  {
    id: 'employee',
    label: 'Employee',
    subject: 'Scope 3.7 — Ask employees about commuting and remote-work energy use',
    Icon: Users,
    iconBg: 'tw:bg-teal-100',
    iconColor: 'tw:text-teal-700',
    titleColor: 'tw:text-teal-900',
  },
  {
    id: 'upstream-leased-assets',
    label: 'Upstream leased assets',
    subject: 'Scope 3.8 — Ask lessors for site-level emissions data on assets you lease',
    Icon: Warehouse,
    iconBg: 'tw:bg-cyan-100',
    iconColor: 'tw:text-cyan-700',
    titleColor: 'tw:text-cyan-900',
  },
  {
    id: 'customer-processing',
    label: 'Customer processing',
    subject: 'Scope 3.10 — Ask customers about further processing of sold products',
    Icon: Factory,
    iconBg: 'tw:bg-amber-100',
    iconColor: 'tw:text-amber-700',
    titleColor: 'tw:text-amber-900',
  },
  {
    id: 'leased-assets',
    label: 'Downstream leased assets',
    subject: 'Scope 3.13 — Ask lessees for energy use of assets you lease out',
    Icon: Building2,
    iconBg: 'tw:bg-sky-100',
    iconColor: 'tw:text-sky-700',
    titleColor: 'tw:text-sky-900',
  },
  {
    id: 'franchisees',
    label: 'Franchisees',
    subject: 'Scope 3.14 — Ask franchisees for site-level emissions data',
    Icon: Store,
    iconBg: 'tw:bg-violet-100',
    iconColor: 'tw:text-violet-700',
    titleColor: 'tw:text-violet-900',
  },
  {
    id: 'investees',
    label: 'Investees',
    subject: 'Scope 3.15 — Ask portfolio companies for their emissions, weighted by your share',
    Icon: PieChart,
    iconBg: 'tw:bg-indigo-100',
    iconColor: 'tw:text-indigo-700',
    titleColor: 'tw:text-indigo-900',
  },
];
